// libraries
import * as React from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrosshairs, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { getEnumStrings } from "@caps-mobile/common-lib";
import { HereMap } from "@algo/here-maps";
// hooks & context
import { useCameraGroup, useCursorOnFeature, useResizeMapOnNavToggle, useSetZoomAndCenter, useWindowResize } from "./hooks";
import { useBreakpoint } from "~/library/useBreakpoint";
import SiteNavContext from "~/navigation/SiteNavContext";
import MapModalContext from "~/contexts/MapModalContext";
// styles
import * as SC from "./Styled";
// types & models
import { EThemeOptions, ThemeContext } from "~/theme";
import { EMapTarget, IMarkerTapObject, IZoomToValue } from "~/interfaces";
// components
import ZoomOptionsModal from "~/components/pages/map/modals/ZoomOptionsModal";
import OverlaySection from "../../map-overlay-views/OverlaySection";
import OverlayButton from "~/components/pages/map/map-overlay-views/overlay-button/OverlayButton";
import SearchBar from "../../search-bar/SearchBar";
import { AnimatePresence, motion } from "framer-motion";
// resources
import { D_MAP_TARGET, NORMAL_VECTOR_DAY_MAP_SCHEME, NORMAL_VECTOR_NIGHT_MAP_SCHEME, ZOOM_TO_VALUES } from "~/constants";
import CameraGroupModal from "./CameraGroupModal";

const TARGETS: string[] = getEnumStrings(EMapTarget);

export type IProps = {
    doneCallback: any;
    handleAdd: (index: number, cameraId: number) => void;
    index: number;
};

export const MapModal: React.FC<IProps> = (props) => {

    const [mapAndPlatform, setMapAndPlatform] = React.useState<[H.Map | undefined, H.service.Platform | undefined]>([undefined, undefined]);
    const navContext: any = React.useContext(SiteNavContext);
    const theme: any = React.useContext(ThemeContext);
    const mapTheme = (theme.mode === EThemeOptions.Dark)
        ? NORMAL_VECTOR_NIGHT_MAP_SCHEME
        : NORMAL_VECTOR_DAY_MAP_SCHEME;

    const settingsStore: any = useSelector((state: any) => state.settings);

    // zoomTo modal
    const [currentZoomTarget, setCurrentZoomTarget] =
        React.useState<EMapTarget>(settingsStore.defaultMapTarget || EMapTarget.alabama);

    const [showZoomOptionsModal, setShowZoomOptionsModal] = React.useState<boolean>(false);

    const zoomTarget: IZoomToValue =
        ZOOM_TO_VALUES[currentZoomTarget] || D_MAP_TARGET;

    //  this value is only used to trigger a map zoom change. The current value will never matter. Just needs to change on click. 
    const [zoomToggled, setZoomToggled] = React.useState<boolean>();

    const { sm: isSmallMax } = useBreakpoint();
    const { md: isMediumMax } = useBreakpoint();

    // resizes map to fit screen and sets zoom when changing screen size
    useWindowResize(mapAndPlatform[0], window.innerWidth, window.innerHeight, zoomTarget, isMediumMax && isSmallMax);

    // The map will not automatically resize when the panel is closed.
    // This effect will wait until the animation has finished and then resize
    useResizeMapOnNavToggle(mapAndPlatform[0], navContext.panelOpen);

    useSetZoomAndCenter(mapAndPlatform, zoomTarget, zoomToggled, isMediumMax && !isSmallMax);

    // causes the cursor to change to pointer when mousing over markers on the map
    useCursorOnFeature(mapAndPlatform[0]);

    const [lat, setLat] = React.useState<number>();
    const [lng, setLng] = React.useState<number>();
    navigator.geolocation.getCurrentPosition(
        (position) => {
            setLat(position.coords.latitude);
            setLng(position.coords.longitude);
        }
    );

    const myLocationEnabled: boolean = lat && lng ? true : false;

    const mapModalContext: any = React.useContext(MapModalContext);
    const [ multipleCamerasObject, setMultipleCamerasObject] = React.useState<IMarkerTapObject | undefined>();

    // Motion animation variants
    const slideVariants = {
        hidden: { x: "100%" }, // Start off-screen (right)
        visible: { x: "0%", transition: { duration: 0.5, ease: "easeInOut" } }, // Slide in
        exit: { x: "100%", transition: { duration: 0.5, ease: "easeInOut" } } // Slide out
    };

    const markerTapHandler = React.useCallback((dataObject: IMarkerTapObject) => {
        // Check if there is more than one camera in the object
        // If so show camera detail modal
        if(dataObject.data.pages[0].items.length > 1) {
            setMultipleCamerasObject(dataObject);
        }
        // If no add camera to item
        else{
            //Close map model
            mapModalContext.setShowModal(false);

            props.handleAdd(props.index, dataObject.data.pages[0].items[0].camera.id);
        }
    }, []);

    const [searchText, setSearchText] = React.useState<string | null>(null);
    useCameraGroup({searchText, map: mapAndPlatform[0], markerTapHandler});


    return (
        <SC.MapModalWrapper>
            <SC.StyledMapModal>
                <OverlaySection
                    top={10} right={10}
                    styleOverwrite={`height: 39px;`}
                    childWrapperStyleOverwrite={`align-items: flex-end'; flex-direction: row; justify-content: flex-end;`}
                >
                    <SearchBar 
                        styleOverwrite={`width: auto; margin-right: 10px; background-color: ${theme.colors["bg-primary"].rgba};`}
                        autoSubmit={true}
                        submitCallback={
                            (evt: any, searchText: string) => 
                                {
                                    setSearchText(searchText);
                                }} />

                    <SC.xIconWrapper onClick={props.doneCallback}>
                        <FontAwesomeIcon
                            className="fa-fw"
                            size={"lg"}
                            icon={faXmark}
                            color={"rgba(235, 235, 245, 0.6)"}
                        />
                    </SC.xIconWrapper>
                </OverlaySection>

                <ZoomOptionsModal
                    modalOpen={showZoomOptionsModal}
                    currentSelection={TARGETS.indexOf(currentZoomTarget)}
                    selectionCallback={(newValue: EMapTarget) => {
                        setCurrentZoomTarget(newValue); setZoomToggled(zoomToggled => !zoomToggled)
                    }}
                    closeCallback={() => setShowZoomOptionsModal(false)}
                    myLocationEnabled={myLocationEnabled} />

                <HereMap scheme={mapTheme}
                    storeMapAndPlatform={
                        (map: H.Map, platform: H.service.Platform) => { setMapAndPlatform([map, platform]); }
                    }
                />

                <OverlaySection
                    bottom={5} right={10}
                    styleOverwrite={`width: auto;`}
                    childWrapperStyleOverwrite={`align-items: flex-end;`}>

                    <OverlayButton callback={() => setShowZoomOptionsModal(true)}
                        label={"Zoom To"} iconProp={faCrosshairs} />

                </OverlaySection>
            </SC.StyledMapModal>

            <AnimatePresence mode="wait">
                {multipleCamerasObject &&
                    <motion.div 
                        key="div2" 
                        initial="hidden" 
                        animate="visible" 
                        exit="exit" 
                        variants={slideVariants}
                        style={{ height: "100%", zIndex: "1", position: "relative" }}>
                        <CameraGroupModal
                            object={multipleCamerasObject} 
                            doneCallback={() => { mapModalContext.setShowModal(false); }}
                            backCallback={() => { setMultipleCamerasObject(undefined); }}
                            objectCallback={(cameraId: number) => {
                                props.handleAdd(props.index, cameraId);
                                mapModalContext.setShowModal(false);
                            }} />
                    </motion.div>
                }
            </AnimatePresence>
        </SC.MapModalWrapper>
    )
};

export default MapModal;
